/**
 * Supabase Quiz Repository
 *
 * Implements IQuizRepository for Supabase storage.
 * Handles quiz attempt CRUD, queries, and real-time subscriptions.
 *
 * @module repositories/supabase/SupabaseQuizRepository
 */
import { getSupabaseClient } from '../../services/auth/supabase';
import type { IQuizRepository, Unsubscribe, SaveAttemptInput } from '../interfaces/IQuizRepository';
import type { QuizAttempt, QuizAnswer } from '../../types/quiz';
import type { Database, Json } from '../../types/database.types';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { clientLogger } from '@/lib/clientLogger';

type QuizAttemptRow = Database['public']['Tables']['quiz_attempts']['Row'];
type QuizAttemptInsert = Database['public']['Tables']['quiz_attempts']['Insert'];

// =============================================================================
// Row Conversion Helpers
// =============================================================================

/**
 * Convert database row to QuizAttempt domain object.
 */
function rowToQuizAttempt(row: QuizAttemptRow): QuizAttempt {
  const answers = row.answers as unknown as QuizAnswer[];

  // Calculate points from answers
  const pointsEarned = answers.filter((a) => a.isCorrect).length;
  const totalPoints = answers.length;

  return {
    id: row.id,
    quizId: row.quiz_id,
    lessonId: row.lesson_id ?? '',
    userId: row.user_id,
    answers,
    score: row.score,
    pointsEarned,
    totalPoints,
    passed: row.passed,
    startedAt: row.created_at, // Using created_at as startedAt
    completedAt: row.created_at, // Attempts are saved on completion
    timeSpent: row.time_taken_seconds ?? 0,
  };
}

/**
 * Convert SaveAttemptInput to database insert row.
 */
function attemptToRow(userId: string, attempt: SaveAttemptInput): QuizAttemptInsert {
  return {
    user_id: userId,
    quiz_id: attempt.quizId,
    lesson_id: attempt.lessonId ?? null,
    module_id: null, // Not tracked in domain type
    pathway_id: null, // Not tracked in domain type
    score: attempt.score,
    passed: attempt.passed,
    answers: attempt.answers as unknown as Json,
    time_taken_seconds: attempt.timeSpent ?? null,
    attempt_number: null, // Will be calculated if needed
  };
}

// =============================================================================
// Repository Implementation
// =============================================================================

export class SupabaseQuizRepository implements IQuizRepository {
  // ===========================================================================
  // Quiz Attempt Operations
  // ===========================================================================

  async saveAttempt(userId: string, attempt: SaveAttemptInput): Promise<QuizAttempt> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    // Get current attempt count for this quiz
    const attemptCount = await this.getAttemptCount(userId, attempt.quizId);

    const insertData: QuizAttemptInsert = {
      ...attemptToRow(userId, attempt),
      attempt_number: attemptCount + 1,
    };

    const { data, error } = await supabase
      .from('quiz_attempts')
      .insert(insertData)
      .select()
      .single();

    if (error) throw new Error(`Failed to save quiz attempt: ${error.message}`);
    return rowToQuizAttempt(data);
  }

  async getQuizAttempts(userId: string, quizId: string): Promise<QuizAttempt[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('quiz_attempts')
      .select('*')
      .eq('user_id', userId)
      .eq('quiz_id', quizId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch quiz attempts: ${error.message}`);
    return data.map(rowToQuizAttempt);
  }

  async getLessonAttempts(userId: string, lessonId: string): Promise<QuizAttempt[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('quiz_attempts')
      .select('*')
      .eq('user_id', userId)
      .eq('lesson_id', lessonId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch lesson attempts: ${error.message}`);
    return data.map(rowToQuizAttempt);
  }

  async getAllAttempts(userId: string): Promise<QuizAttempt[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('quiz_attempts')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch all attempts: ${error.message}`);
    return data.map(rowToQuizAttempt);
  }

  async getBestAttempt(userId: string, quizId: string): Promise<QuizAttempt | null> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('quiz_attempts')
      .select('*')
      .eq('user_id', userId)
      .eq('quiz_id', quizId)
      .order('score', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to fetch best attempt: ${error.message}`);
    }
    if (!data) return null;

    return rowToQuizAttempt(data);
  }

  async getLatestAttempt(userId: string, quizId: string): Promise<QuizAttempt | null> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('quiz_attempts')
      .select('*')
      .eq('user_id', userId)
      .eq('quiz_id', quizId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to fetch latest attempt: ${error.message}`);
    }
    if (!data) return null;

    return rowToQuizAttempt(data);
  }

  async hasPassedQuiz(userId: string, quizId: string, passingScore: number): Promise<boolean> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('quiz_attempts')
      .select('id')
      .eq('user_id', userId)
      .eq('quiz_id', quizId)
      .gte('score', passingScore)
      .limit(1);

    if (error) throw new Error(`Failed to check quiz pass status: ${error.message}`);
    return data.length > 0;
  }

  async getAttemptCount(userId: string, quizId: string): Promise<number> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { count, error } = await supabase
      .from('quiz_attempts')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('quiz_id', quizId);

    if (error) throw new Error(`Failed to get attempt count: ${error.message}`);
    return count ?? 0;
  }

  // ===========================================================================
  // Real-time Subscriptions
  // ===========================================================================

  subscribe(userId: string, callback: (attempts: QuizAttempt[]) => void): Unsubscribe {
    const supabase = getSupabaseClient();
    if (!supabase) {
      clientLogger.warn('[SupabaseQuizRepository] Cannot subscribe: Supabase not configured');
      callback([]);
      return () => {};
    }

    let channel: RealtimeChannel | null = null;

    // Initial fetch
    this.getAllAttempts(userId)
      .then(callback)
      .catch((err) => {
        clientLogger.error('[SupabaseQuizRepository] Initial fetch failed:', err);
        callback([]);
      });

    // Set up real-time subscription
    channel = supabase
      .channel(`quiz_attempts:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'quiz_attempts',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Re-fetch all attempts on any change
          this.getAllAttempts(userId)
            .then(callback)
            .catch((err) => clientLogger.error('[SupabaseQuizRepository] Refetch failed:', err));
        }
      )
      .subscribe();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }

  subscribeToQuiz(
    userId: string,
    quizId: string,
    callback: (attempts: QuizAttempt[]) => void
  ): Unsubscribe {
    const supabase = getSupabaseClient();
    if (!supabase) {
      clientLogger.warn('[SupabaseQuizRepository] Cannot subscribe: Supabase not configured');
      callback([]);
      return () => {};
    }

    let channel: RealtimeChannel | null = null;

    // Initial fetch
    this.getQuizAttempts(userId, quizId)
      .then(callback)
      .catch((err) => {
        clientLogger.error('[SupabaseQuizRepository] Initial fetch failed:', err);
        callback([]);
      });

    // Set up real-time subscription
    channel = supabase
      .channel(`quiz_attempts:${userId}:${quizId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'quiz_attempts',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Re-fetch quiz attempts on any change
          this.getQuizAttempts(userId, quizId)
            .then(callback)
            .catch((err) => clientLogger.error('[SupabaseQuizRepository] Refetch failed:', err));
        }
      )
      .subscribe();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }
}
